import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { LocationOn, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Button, Chip, Typography, useTheme, Tooltip } from '@mui/material';
import { NimbuxHotel } from '@bandido/shared/interfaces';

import { amenities } from '@frontend/assets/amenities';
import { HotelLinkModal } from "@frontend/features/hotel-content-manager/components/HotelLinkModal";

export interface NimbuxContentHotelCardProps {
  data: NimbuxHotel;
  editRoute?: string;
}

export const NimbuxContentHotelCard = ({
  data,
  editRoute,
}: NimbuxContentHotelCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % data.medias.length);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + data.medias.length) % data.medias.length);
  };

  const handleOpenLinkModal = () => {
    setLinkModalOpen(true);
  };

  const handleCloseLinkModal = () => {
    setLinkModalOpen(false);
  };

  const renderImageCarousel = () => {
    if (!data.medias || data.medias.length === 0) return null;

    return (
      <Box sx={{ position: 'relative', width: 319, height: 288, overflow: 'hidden' }}>
        {data.medias.map((media, index) => (
          <Box
            key={index}
            component="img"
            src={media.url}
            alt={`${data.name} - Image ${index + 1}`}
            loading="lazy"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px 0 0 8px',
              opacity: index === currentImageIndex ? 1 : 0,
              transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />
        ))}

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 80,
            background: 'linear-gradient(to top, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.08) 25%, rgba(0,0,0,0.04) 50%, rgba(0,0,0,0.02) 75%, transparent 100%)',
            borderRadius: '0 0 0 8px',
            pointerEvents: 'none',
          }}
        />

        {data.medias.length > 1 && (
          <>
            {currentImageIndex > 0 && (
              <Box
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '40%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  paddingLeft: 2,
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                  background: 'linear-gradient(to right, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.05) 50%, transparent 100%)',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                <ChevronLeft
                  sx={{
                    color: 'white',
                    fontSize: 28,
                    filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))',
                  }}
                />
              </Box>
            )}

            {currentImageIndex < data.medias.length - 1 && (
              <Box
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '40%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: 2,
                  opacity: 0,
                  transition: 'opacity 0.2s ease-in-out',
                  background: 'linear-gradient(to left, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.05) 50%, transparent 100%)',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                <ChevronRight
                  sx={{
                    color: 'white',
                    fontSize: 28,
                    filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.3))',
                  }}
                />
              </Box>
            )}
          </>
        )}

        {data.medias.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 0.5,
              zIndex: 1,
            }}
          >
            {data.medias.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === currentImageIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // const renderStars = () => {
  //   const stars = [];
  //   for (let i = 0; i < 5; i++) {
  //     stars.push(
  //       <Star
  //         key={i}
  //         sx={{
  //           color: i < data?.rank ? theme.palette.secondary.main : theme.palette.grey[300]
  //         }}
  //       />
  //     );
  //   }
  //   return stars;
  // };

  const getStatusConfig = () => {
    switch (data.status) {
      case 'markedForModeration':
        return { color: 'warning' as const, variant: 'outlined' as const };
      case 'moderated':
        return { color: 'success' as const, variant: 'filled' as const };
      case 'toReview':
        return { color: 'error' as const, variant: 'outlined' as const };
      case 'reviewed':
        return { color: 'success' as const, variant: 'filled' as const };
      default:
        return { color: 'default' as const, variant: 'outlined' as const };
    }
  };

  const renderAmenities = () => data.amenities.slice(0, 6).map((amenityKey, index) => {
    const AmenityIcon = amenities[amenityKey.icon ?? ''];
    if (!AmenityIcon) return null;

    const amenityTranslation = t(`hotels.amenities.${amenityKey}`, { defaultValue: amenityKey });

    return (
      <Tooltip key={index} title={amenityTranslation} arrow>
        <Box
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.primary.states?.focus,
            borderRadius: '50%',
            color: theme.palette.primary.main,
            cursor: 'pointer',
            '& svg': {
              fill: theme.palette.primary.main,
              '& path': {
                fill: theme.palette.primary.main
              }
            }
          }}
        >
          <AmenityIcon />
        </Box>
      </Tooltip>
    );
  });

  const statusConfig = getStatusConfig();
  const lastModificationDate = dayjs(data.updatedAt).format('DD/MM/YYYY');

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'stretch',
      alignSelf: 'stretch',
      borderRadius: 2,
      boxShadow: theme.shadows[4],
      backgroundColor: 'background.paper',
      width: '100%',
      minHeight: 288,
      maxWidth: 782
    }}>
      {renderImageCarousel()}
      <Box flex={1} px={2} py={1} gap={2} display="flex" flexDirection="column" justifyContent="space-between" alignSelf="stretch">
        <Box>
          <Typography variant="subtitle1" color="text.secondary" sx={{
            textTransform: 'capitalize',
          }}>{data.name?.toLowerCase()}</Typography>
          {/* <Box display="flex" alignItems="center" gap={1}>
            <Box>
              {renderStars()}
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body1" color="text.primary">{data.rank}</Typography>
              <Typography variant="caption" color="text.disabled">
                {t('hotelContent.nimbuxHotels.card.opinions', { count: data.impressions })}
              </Typography>
            </Box>
          </Box> */}
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={1} textAlign="left">
            <LocationOn fontSize="small" sx={{ color: theme.palette.text.disabled }} />
            <Typography variant="caption" color="text.disabled">{data.address}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {renderAmenities()}
            {data.amenities.length > 6 && (
              <Typography variant="caption" color="text.disabled">
                +{data.amenities.length - 6} más
              </Typography>
            )}
          </Box>
          <Box borderTop={`1px solid ${theme.palette.divider}`} width="100%" py={1} display="flex" justifyContent="space-between">
            <Box display="flex" gap={1} flexDirection="column">
              <Typography variant="body2" color="text.disabled">
                {t('hotelContent.nimbuxHotels.card.lastModeration', { date: lastModificationDate })}
              </Typography>
              <Tooltip title={t(`hotelContent.nimbuxHotels.card.status.${data.status}`)} arrow>
                <Chip
                  label={t(`hotelContent.nimbuxHotels.card.status.${data.status}`)}
                  size="small"
                  color={statusConfig.color}
                  variant={statusConfig.variant}
                  sx={{ width: 'fit-content', maxWidth: 150, cursor: 'pointer' }}
                />
              </Tooltip>
            </Box>
            <Box display="flex" flexDirection="column" justifyContent="flex-end" alignSelf="stretch">
              <Box display="flex" gap={1}>
                <Button variant="contained" color="secondary" onClick={handleOpenLinkModal}>
                  {t('hotelContent.nimbuxHotels.card.link')}
                </Button>
                {editRoute && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    component={Link}
                    to={editRoute}
                    sx={{ textDecoration: 'none' }}
                  >
                    {t('hotelContent.nimbuxHotels.card.editButton')}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <HotelLinkModal
        open={!!data.id && linkModalOpen}
        onClose={handleCloseLinkModal}
        hotelName={data.name!}
        nimbuxParentId={data.id!}
      />
    </Box>
  );
};
